import { Check, X } from 'lucide-react';
import { Button } from '../ui/Button';
import type { Run } from './importStore';
import type { Step, StepState } from './run';
import './ImportProgress.css';

/**
 * The step indicator, in the panel, while a link import runs.
 *
 * Progress matters here: this takes several seconds and silence reads as
 * breakage. Once it's done it stays a moment longer to report anything the
 * page wouldn't give up, then the user dismisses it.
 */

const LABELS: Record<Step, string> = {
  fetch: 'Fetching page',
  read: 'Reading details',
  save: 'Saving photos',
};

const ORDER: readonly Step[] = ['fetch', 'read', 'save'];

export function ImportProgress({
  run,
  onDismiss,
  onPaste,
}: {
  run: Run;
  onDismiss: () => void;
  /** Offered when the fetch was blocked — the way past a bot wall. */
  onPaste: () => void;
}) {
  if (run.error !== undefined) {
    return (
      <section className="import" role="alert">
        <p className="import__error">{run.error}</p>

        <div className="import__actions">
          {run.blocked && (
            <Button variant="quiet" onClick={onPaste}>
              Paste Page Instead
            </Button>
          )}
          <Button variant="quiet" onClick={onDismiss}>
            Dismiss
          </Button>
        </div>
      </section>
    );
  }

  if (run.finished) {
    return (
      <section className="import" role="status">
        <p className="import__done">
          {run.failedImages === 0
            ? 'Imported. Everything is saved on this machine.'
            : `Imported. ${run.failedImages} ${
                run.failedImages === 1 ? 'photo' : 'photos'
              } couldn’t be saved.`}
        </p>

        <div className="import__actions">
          <Button variant="quiet" onClick={onDismiss}>
            Dismiss
          </Button>
        </div>
      </section>
    );
  }

  return (
    <section className="import" role="status" aria-busy="true">
      <ol className="import__steps">
        {ORDER.map((step) => (
          <li key={step} className={`import__step import__step--${run.progress.steps[step]}`}>
            <span className="import__mark" aria-hidden="true">
              <Mark state={run.progress.steps[step]} />
            </span>

            <span className="import__label">
              {LABELS[step]}
              {step === 'save' && run.progress.saved !== undefined
                ? ` ${run.progress.saved.done} of ${run.progress.saved.total}`
                : '…'}
            </span>
          </li>
        ))}
      </ol>
    </section>
  );
}

function Mark({ state }: { state: StepState }) {
  if (state === 'done') return <Check size={14} />;
  if (state === 'failed') return <X size={14} />;
  // Waiting and running share a dot; the CSS animates the running one.
  return <span className="import__dot" />;
}
